import React, {useEffect, useMemo} from 'react'
import styles from './UIGamePlayerCardInventory.module.scss'
import clsx from 'clsx'
import {CARD_POPUP_BODY, cardEnumsToCardState} from '../../utils/cards.ts'
import {UIGameCardInventory} from '../../common/UIGameCardInventory/UIGameCardInventory.tsx'
import {useGameStore} from '../../store/GameStoreContext.ts'
import {CardEnum} from '@colonist/game-states'
import {ArrayUtils} from '@colonist/utils'
import {TradeErrorHighlightLocationEnum, Point} from '@colonist/game-mechanic-validators'
import {type TranslationObject} from '@colonist/i18n'
import {getCardsToGreyForBankTrade, removeCardsSelected, getCanClickCardDataForAllCards} from './utils.ts'
import {usePlayerValidators} from '../../store/selectors/usePlayerValidators.ts'
import {useIsReplay} from '../../store/selectors/useIsReplay.ts'
import {GameAction, ModeSettingEnum} from '@colonist/game-base-types'
import {CardClickPopupType, CardDataController, type CardPopupInformation, isDevelopmentCard} from '@colonist/game-card-types'
import {useUIGameSocketContext} from '../../contexts/UIGameSocketContext.tsx'
import {useCurrentStateValidator} from '../../store/selectors/useCurrentStateValidator.ts'
import {useMechanicValidatorDevelopmentCard} from '../../store/selectors/useMechanicValidatorDevelopmentCard.ts'

/**
 * Gets the HTML position of a card in the player's card inventory
 * @param cardEnum - The card to find the position for
 * @returns Point with coordinates relative to the ui-game container, or undefined if not found
 */
export function getPointPositionFromPlayerInventory(cardEnum: CardEnum): Point | undefined {
    const playerInventory = document.getElementById('player-card-inventory') as HTMLElement | null
    if(!playerInventory) return undefined

    const uiGameContainer = document.getElementById('ui-game') as HTMLElement | null
    if(!uiGameContainer) return undefined

    const uiGameRect = uiGameContainer.getBoundingClientRect()

    const cardElement = playerInventory.querySelector(`[data-card-enum="${cardEnum}"]`) as HTMLElement | null
    if(!cardElement) {
        const inventoryRect = playerInventory.getBoundingClientRect()
        const inventoryCenterX = inventoryRect.left - uiGameRect.left
        const inventoryTopY = inventoryRect.top - uiGameRect.top

        return new Point(inventoryCenterX, inventoryTopY)
    }

    const cardRect = cardElement.getBoundingClientRect()
    const cardCenterX = cardRect.left + cardRect.width / 2 - uiGameRect.left
    const cardTopY = cardRect.top - uiGameRect.top
    return new Point(cardCenterX, cardTopY)
}

/**
 * Gets the dimensions of a card from the player's card inventory
 * @returns Object with width and height of a card
 */
export function getCardDimensionsFromPlayerInventory(): {width: number, height: number} {
    const playerInventory = document.getElementById('player-card-inventory') as HTMLElement | null
    if(!playerInventory) return {width: 0, height: 0}

    const cardElement = playerInventory.querySelector('[data-card-enum]') as HTMLElement | null

    if(!cardElement) return {width: 0, height: 0}

    const cardRect = cardElement.getBoundingClientRect()
    return {
        width: cardRect.width,
        height: cardRect.height,
    }
}

export interface UIGamePlayerCardInventoryProps {
    className?: string | undefined
    background?: 'beige' | 'dark'
    disableTooltip?: boolean | undefined
    disableCardScaleOnHover?: boolean | undefined
}

export const UIGamePlayerCardInventory = ({
    className,
    background = 'beige',
    disableTooltip = false,
    disableCardScaleOnHover = false,
}: UIGamePlayerCardInventoryProps) => {
    const setGameCardInventory = useGameStore((state) => state.gameCardInventory.setCardInventory)
    const selectedCards = useGameStore((state) => state.gameCardInventory.selectedCards)
    const cardsAnimating = useGameStore((state) => state.gameCardAnimator.cardsAnimating)
    const cardsAnimatingToMyHand = useMemo(() =>
        cardsAnimating.filter((card) => card.isAnimatingToMyHand).map((card) => card.card.cardEnum),
    [cardsAnimating])
    const cardsToDisableWhenPickingForActionBox = useGameStore((state) => state.gameCardInventory.cardsToDisable)
    const onSelectCardForTrade = useGameStore((state) => state.gameCardInventory.onSelectCardForTrade)
    const onSelectCardForActionBox = useGameStore((state) => state.gameCardInventory.onSelectCardForActionBox)
    const actionBoxType = useGameStore((state) => state.actionBox.actionBoxData?.type)
    const gameState = useGameStore((state) => state.gameState)
    const {getPlayerValidator} = usePlayerValidators()
    const myColor = useGameStore((state) => state.myColor)
    const isBankTradeMode = useGameStore((state) => state.gameTrade.isBankTradeMode)
    const isShowTradePanel = useGameStore((state) => state.gameTrade.isShowTradePanel)
    const tradeError = useGameStore((state) => state.gameTrade.tradeError)
    const translationKeyContext = useGameStore((state) => state.gameMode.translationKeyContext)
    const beginnerActionState = useGameStore((state) => state.gameBeginnerHint.beginnerActionState)
    const beginnerHintData = useGameStore((state) => state.gameBeginnerHint.beginnerHintData)
    const modeSetting = useGameStore((state) => state.gameSettings.modeSetting)
    const isReplay = useIsReplay()
    const {sendClickedDevelopmentCard, sendRequestActionSwap, sendConfirmUseDevelopmentCard} = useUIGameSocketContext()
    const currentStateValidator = useCurrentStateValidator()
    const mechanicValidatorDevelopmentCard = useMechanicValidatorDevelopmentCard()

    const isBeginnerMode = modeSetting == ModeSettingEnum.BeginnerModeClassic4P
    const offeredResourcesSuggestion: CardEnum[] = beginnerHintData?.data.offeredResources != null ? beginnerHintData?.data.offeredResources : []
    const shouldShowSuggestion = offeredResourcesSuggestion.length > 0 && isShowTradePanel
    const myPlayerValidator = getPlayerValidator(myColor)
    const myBankTradeRatioState = myPlayerValidator.bankTradeRatiosState
    const isActionBoxPickCardOpening = actionBoxType === 'pickCard'

    const myResourceCards = myPlayerValidator.resourceCards.cards
    const myDevelopmentCards = gameState.mechanicDevelopmentCardsState?.players?.[myColor]?.developmentCards.cards ?? []
    const myProgressCards = gameState.mechanicProgressCardsState?.players?.[myColor]?.progressCards.cards ?? []
    const anyResourceCards = isShowTradePanel && !isBankTradeMode && !isBeginnerMode ? [CardEnum.AnyResource] : []

    const allCardsInInventory = useMemo(() => [
        ...myResourceCards,
        ...myDevelopmentCards,
        ...myProgressCards,
        ...anyResourceCards,
    ], [myResourceCards, myDevelopmentCards, myProgressCards, anyResourceCards])

    const allVisibleCards = useMemo(() => {
        const cardsToRemove = [...cardsAnimatingToMyHand, ...selectedCards]
        return removeCardsSelected(allCardsInInventory, cardsToRemove)
    }, [allCardsInInventory, cardsAnimatingToMyHand, selectedCards])

    useEffect(() => {
        setGameCardInventory(allCardsInInventory)
    }, [allCardsInInventory])

    const cardState = useMemo(() => cardEnumsToCardState(allVisibleCards), [allVisibleCards])

    const canClickCardDataForAllCards = useMemo(() => getCanClickCardDataForAllCards({
        allMyCards: allVisibleCards,
        currentState: gameState.currentState,
        isReplay: isReplay,
        myColor: myColor,
        translationKeyContext: translationKeyContext,
        isShowTradePanel: isShowTradePanel,
        isActionBoxPickCardOpening: isActionBoxPickCardOpening,
        currentStateValidator: currentStateValidator,
    }), [
        allVisibleCards,
        gameState.currentState,
        isReplay,
        myColor,
        translationKeyContext,
        isShowTradePanel,
        isActionBoxPickCardOpening,
        currentStateValidator,
    ])

    const canPlayDevelopmentCard = (cardEnum: CardEnum): boolean => {
        if(!isDevelopmentCard(cardEnum)) return true
        if(!mechanicValidatorDevelopmentCard) return true

        const canClickCardData = canClickCardDataForAllCards[cardEnum]
        if(!canClickCardData?.canClickCard) return false

        const canUseDevelopmentCard = mechanicValidatorDevelopmentCard.canPlayerUseDevelopmentCard(myColor, cardEnum)
        if(!canUseDevelopmentCard.canTakeAction) return false

        return true
    }

    const unplayableDevelopmentCards = useMemo(() => {
        if(!mechanicValidatorDevelopmentCard || isShowTradePanel) return []

        const unplayableCards: CardEnum[] = []
        for(const devCard of myDevelopmentCards) {
            if(!canPlayDevelopmentCard(devCard)) {
                unplayableCards.push(devCard)
            }
        }
        return unplayableCards
    }, [mechanicValidatorDevelopmentCard, myDevelopmentCards, myColor, isShowTradePanel, canClickCardDataForAllCards])

    const cardsToDisable = useMemo(() => {
        if(cardsToDisableWhenPickingForActionBox != undefined && isActionBoxPickCardOpening) return cardsToDisableWhenPickingForActionBox
        let cardsToGrey: CardEnum[] = []

        if(isShowTradePanel) {
            cardsToGrey = [...myDevelopmentCards, ...myProgressCards]
        }

        if(isShowTradePanel && isBankTradeMode) {
            cardsToGrey.push(...getCardsToGreyForBankTrade(myBankTradeRatioState, cardState))
        }

        if(!isShowTradePanel) {
            cardsToGrey.push(...unplayableDevelopmentCards)
        }

        return cardEnumsToCardState(cardsToGrey)
    }, [
        myDevelopmentCards,
        myProgressCards,
        myBankTradeRatioState,
        cardsToDisableWhenPickingForActionBox,
        isShowTradePanel,
        isBankTradeMode,
        cardState,
        isActionBoxPickCardOpening,
        unplayableDevelopmentCards,
    ])

    const cardsToHighlight = useMemo(() => {
        const tradeErrorHighlightCards = tradeError?.payload?.highlightCards?.cardsToHighlight ?? []
        const tradeErrorHighlightLocations = tradeError?.payload?.highlightCards?.locations ?? []
        if(tradeErrorHighlightLocations.includes(TradeErrorHighlightLocationEnum.OfferedSelector)) {
            return tradeErrorHighlightCards
        }
        return []
    }, [tradeError])

    const cardsToScaleOnHover = useMemo(() => {
        if(isShowTradePanel || disableCardScaleOnHover) return []
        const cardsToScale = [...myProgressCards]
        ArrayUtils.getUniqueElements(cardsToScale)
        return cardsToScale
    }, [myProgressCards, isShowTradePanel, disableCardScaleOnHover])

    const cardsHelperText = useMemo(() => {
        const cardsHelperTextLocal: {[key in CardEnum]?: TranslationObject} = {}
        for(const cardEnum in canClickCardDataForAllCards) {
            cardsHelperTextLocal[cardEnum] = canClickCardDataForAllCards[cardEnum].helperText
        }

        if(mechanicValidatorDevelopmentCard) {
            for(const devCard of myDevelopmentCards) {
                const canUseDevelopmentCard = mechanicValidatorDevelopmentCard.canPlayerUseDevelopmentCard(myColor, devCard)
                if(!canUseDevelopmentCard.canTakeAction && canUseDevelopmentCard.errorMessageIfCantTakeAction) {
                    cardsHelperTextLocal[devCard] = canUseDevelopmentCard.errorMessageIfCantTakeAction
                }
            }
        }

        return cardsHelperTextLocal
    }, [canClickCardDataForAllCards, mechanicValidatorDevelopmentCard, myDevelopmentCards, myColor])

    const cardsToShowTooltip = useMemo(() => {
        if(!isActionBoxPickCardOpening) return undefined

        const progressCardsNotGreyedOut = myProgressCards.filter((cardEnum) => {
            const greyedOutCount = cardsToDisable[cardEnum] ?? 0
            const totalCount = cardState[cardEnum] ?? 0
            return greyedOutCount < totalCount
        })

        return progressCardsNotGreyedOut
    }, [isActionBoxPickCardOpening, myProgressCards, cardsToDisable, cardState])

    const handleCreateCardConfirmationPopup = (cardEnum: CardEnum) => {
        sendRequestActionSwap(GameAction.ClickedDevelopmentCard)
        const popupInformation: CardPopupInformation = {
            cardEnum: cardEnum,
            cardBody: CARD_POPUP_BODY[cardEnum],
        }
        sendConfirmUseDevelopmentCard(popupInformation)
    }

    const handleClickCard = (cardEnum: CardEnum) => {
        const canClickCardData = canClickCardDataForAllCards[cardEnum]
        if(!canClickCardData?.canClickCard) return

        const isNotBeginnerGameActionClickedDevCard = beginnerActionState != null && beginnerActionState?.type != GameAction.ClickedDevelopmentCard
        const isNotBeginnerGameActionTrade = beginnerActionState != null && beginnerActionState?.type != GameAction.CreateTrade
        if(actionBoxType === 'pickCard') {
            onSelectCardForActionBox?.(cardEnum)
            return
        }

        if(isShowTradePanel) {
            if(isNotBeginnerGameActionTrade) {
                return
            }
            onSelectCardForTrade?.(cardEnum)
            return
        }

        if(!canPlayDevelopmentCard(cardEnum)) return

        const cardData = CardDataController.getCardDataForCard(cardEnum)
        if(cardData?.clickPopupType == CardClickPopupType.NormalClick) {
            if(isNotBeginnerGameActionClickedDevCard) {
                return
            }
            sendClickedDevelopmentCard(cardEnum)
            return
        }
        if(cardData?.clickPopupType == CardClickPopupType.TradeWindow) {
            if(isNotBeginnerGameActionTrade) {
                return
            }
            onSelectCardForTrade?.(cardEnum)
            return
        }
        if(cardData?.clickPopupType == CardClickPopupType.ConfirmationPopup) {
            if(isNotBeginnerGameActionClickedDevCard) {
                return
            }
            handleCreateCardConfirmationPopup(cardEnum)
            return
        }
    }

    const shouldShowBankTradeRatio = isBankTradeMode && isShowTradePanel
    const tooltipEnabled = !disableTooltip && !isShowTradePanel

    return (
        <div className={clsx(styles.container, className, styles[background])}>
            <UIGameCardInventory
                onClickCard={handleClickCard}
                cardState={cardState}
                className={styles.inventory}
                showTipTitle={tooltipEnabled}
                showTipBody={tooltipEnabled}
                cardsToDisable={cardsToDisable}
                cardsToHighlight={cardsToHighlight}
                cardsToSuggestion={shouldShowSuggestion ? offeredResourcesSuggestion : []}
                cardsToScaleOnHover={cardsToScaleOnHover}
                cardsHelperText={cardsHelperText}
                bankTradeRatiosState={shouldShowBankTradeRatio ? myBankTradeRatioState : undefined}
                cardsToShowTooltip={cardsToShowTooltip}
                id="player-card-inventory"/>
        </div>
    )
}
